import { Broadcast } from "./Broadcast"

/**
 * Creates a replay broadcast stream that maintains memory of all emitted messages.
 * New subscribers receive all past messages before getting new ones.
 * Similar to Broadcast but with complete message history.
 *
 * @returns BroadcastInterface with push, stop, and subscribe methods
 *
 * @example
 * ```ts
 * import { ReplayBroadcast } from "@pdfking/tfunc"
 *
 * const replay = ReplayBroadcast<string>()
 *
 * // Push some messages
 * replay.push("message 1")
 * replay.push("message 2")
 *
 * // New subscribers get all past messages
 * const sub = replay.subscribe()
 * for await (const msg of sub) {
 *   console.log(msg) // Outputs: "message 1", "message 2", then future messages
 * }
 *
 * // New messages are sent to all subscribers
 * replay.push("message 3")
 * ```
 */
export const ReplayBroadcast = <T>(): ReturnType<typeof Broadcast<T>> => {
  const messages: T[] = []
  const { stop, push, subscribe } = Broadcast<T>(messages)

  // Return broadcast instance with overridden push
  return {
    subscribe,
    stop,
    push: item => {
      messages.push(item)
      push(item)
    },
  }
}
